/**
 * BillingPage — /billing
 *
 * Shows the org's current plan, trial countdown, and upgrade/downgrade options.
 * When Stripe is configured, upgrade buttons open a hosted Checkout session.
 * When Stripe is not configured (dev/standalone), buttons show a "not configured" message.
 */
import { useState, useEffect } from "react"
import { useSearchParams } from 'react-router-dom'
import { Check, Zap, ExternalLink, AlertTriangle, Crown } from 'lucide-react'
import { usePlan } from '../hooks/usePlan'
import { billingService, PLAN_META, BillingStatus } from '../services/billing'
import { toast } from 'sonner'
import { apiErrorMessage } from '../utils/apiError'

// ── Plan definitions ──────────────────────────────────────────────────────────

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: '$6',
    period: '/user/month',
    target: 'Solo PM or tiny team',
    features: [
      '5 users',
      '10 projects',
      'Tasks & time tracking',
      '1 industry pack',
      'Basic notifications',
    ],
    notIncluded: ['Custom fields', 'Report exports', 'Guest collaborators'],
  },
  {
    key: 'growth',
    name: 'Growth',
    price: '$10',
    period: '/user/month',
    target: 'Teams of 5–50',
    popular: true,
    features: [
      '50 users',
      'Unlimited projects',
      'Custom fields & evidence',
      'PDF / XLSX / PPTX exports',
      '10 guest collaborators',
      'Calendar sync & email-in',
      'Resource-scoped ACLs',
    ],
    notIncluded: [],
  },
  {
    key: 'business',
    name: 'Business',
    price: '$20',
    period: '/user/month',
    target: 'Teams of 50–200',
    features: [
      'Unlimited users',
      'SAML SSO + SCIM',
      'SOC2 evidence package',
      'Audit log export',
      'Unlimited guest collaborators',
      'API access',
      'Priority support',
    ],
    notIncluded: [],
  },
]

// ── Component ─────────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { status, loading, refresh } = usePlan()
  const [searchParams] = useSearchParams()
  const [upgrading, setUpgrading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)

  // Handle Stripe redirect back
  useEffect(() => {
    if (searchParams.get('success') === '1') {
      toast.success('Payment successful! Your plan has been upgraded.')
      refresh()
    } else if (searchParams.get('canceled') === '1') {
      toast('Checkout cancelled. No charges were made.')
    }
  }, [searchParams])

  // Auto-highlight a plan if URL has ?plan=xxx
  const highlightPlan = searchParams.get('plan')

  const handleUpgrade = async (tier: string) => {
    if (!status?.stripe_enabled) {
      toast.info('Stripe billing is not configured on this instance. Contact support to upgrade.')
      return
    }
    setUpgrading(tier)
    try {
      const result = await billingService.createCheckout(tier)
      if (result.url) {
        window.location.href = result.url
      } else {
        toast.error('Could not create checkout session. Please try again.')
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Something went wrong. Please try again.'))
    } finally {
      setUpgrading(null)
    }
  }

  const handlePortal = async () => {
    setPortalLoading(true)
    try {
      const result = await billingService.openPortal()
      if (result.url) {
        window.open(result.url, '_blank')
      } else {
        toast.info('Customer portal is not available. Contact support@karyva.com')
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Something went wrong.'))
    } finally {
      setPortalLoading(false)
    }
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading billing info…</div>

  const currentTier = status?.plan_tier ?? 'trial'

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

      {/* ── Current plan card ── */}
      <CurrentPlanCard status={status} onPortal={handlePortal} portalLoading={portalLoading} />

      {/* ── Expired warning ── */}
      {status?.trial_expired && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Your trial has ended</p>
            <p className="text-sm mt-0.5">
              Your data is safe. Upgrade below to resume full access.
              Questions? Email <a href="mailto:support@karyva.com" className="underline">support@karyva.com</a>
            </p>
          </div>
        </div>
      )}

      {/* ── Plan grid ── */}
      <div>
        <h2 className="text-lg font-bold text-foreground mb-4">Choose a plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map(plan => {
            const isCurrent = currentTier === plan.key
            const isHighlighted = highlightPlan === plan.key

            return (
              <div
                key={plan.key}
                className={`rounded-2xl border p-6 flex flex-col gap-4 transition-all
                  ${plan.popular || isHighlighted
                    ? 'border-violet-400 shadow-lg shadow-violet-100'
                    : 'border-border'}
                  ${isCurrent ? 'ring-2 ring-violet-500' : ''}`}
              >
                {plan.popular && (
                  <span className="self-start text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700">
                    Most popular
                  </span>
                )}

                <div>
                  <p className="font-bold text-foreground text-lg">{plan.name}</p>
                  <div className="flex items-end gap-0.5 mt-1">
                    <span className="text-3xl font-extrabold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground text-sm mb-1">{plan.period}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{plan.target}</p>
                </div>

                <ul className="space-y-2 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground">
                      <Check size={14} className="text-violet-600 shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                  {plan.notIncluded.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground line-through">
                      <span className="w-3.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-violet-50 text-violet-700 text-sm font-semibold">
                    <Crown size={14} />
                    Current plan
                  </div>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={upgrading === plan.key}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors
                      ${plan.popular || isHighlighted
                        ? 'bg-violet-600 hover:bg-violet-700 text-white'
                        : 'bg-muted hover:bg-muted/80 text-foreground'}`}
                  >
                    {upgrading === plan.key ? 'Loading…' : <><Zap size={14} /> Upgrade to {plan.name}</>}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Enterprise CTA ── */}
      <div className="rounded-2xl border border-border bg-muted/30 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-foreground">Need an enterprise or on-premise deployment?</p>
          <p className="text-sm text-muted-foreground mt-1">
            Custom contracts, SCIM provisioning, dedicated infrastructure, and FedRAMP-ready options available.
          </p>
        </div>
        <a
          href="mailto:enterprise@karyva.com"
          className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Contact sales <ExternalLink size={13} />
        </a>
      </div>
    </div>
  )
}

// ── Current plan summary card ─────────────────────────────────────────────────

function CurrentPlanCard({
  status,
  onPortal,
  portalLoading,
}: {
  status: BillingStatus | null
  onPortal: () => void
  portalLoading: boolean
}) {
  if (!status) return null

  const tier = status.plan_tier
  const meta = PLAN_META[tier] ?? PLAN_META.trial
  const daysLeft = status.trial_days_remaining

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
               style={{ background: meta.color }}>
            {meta.label[0]}
          </div>
          <div>
            <p className="font-bold text-foreground">{meta.label} plan</p>
            <p className="text-sm text-muted-foreground">
              {tier === 'trial' && daysLeft !== null
                ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining in your trial`
                : tier === 'trial_expired'
                ? 'Trial ended — upgrade to continue'
                : status.is_paid
                ? `${meta.price} · Billed monthly`
                : meta.price}
            </p>
          </div>
        </div>
        {status.is_paid && status.stripe_customer_id && (
          <button
            onClick={onPortal}
            disabled={portalLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium
                       hover:bg-muted transition-colors"
          >
            <ExternalLink size={14} />
            {portalLoading ? 'Loading…' : 'Manage subscription'}
          </button>
        )}
      </div>
    </div>
  )
}
